// src/services/storage/masterCache.ts

/**
 * マスタ系キャッシュの保存形式ヘルパー。
 *
 * 素の cache-first + revalidate だと、画面を開くたびに裏の再取得が走る
 * （拡張フィールドは7画面、ランクは4箇所、キーワードは5箇所から呼ばれる）。
 * マスタの変更頻度は極めて低いので、取得時刻を一緒に保存して再取得に間隔を設ける。
 *
 * 通信量ではなく「往復そのもの」が減るため、レイテンシが支配的な低速回線で効く。
 */

import { indexedDBStorage } from './indexedDB';

/**
 * マスタ再取得の間隔。
 *
 * acs-light の API2 認証結果キャッシュ（Constants.API2_USERDATA_CACHE_TTL_SECONDS = 300）と揃えている。
 */
export const MASTER_CACHE_TTL_MS = 5 * 60 * 1000;

/**
 * IndexedDB に保存する形。
 * 元の値を data に包み、取得時刻を兄弟キーとして持つ（後ろに足すのではなく1段包む）。
 */
interface StoredMasterCache<T> {
  data: T;
  /** 取得時刻（Date.now() の値＝UTCのエポックミリ秒。端末の時計であることに注意）。 */
  fetchedAt: number;
}

/** getMasterCache の返り値。 */
export interface MasterCacheResult<T> {
  data: T;
  /** TTL を過ぎている（＝裏で再取得すべき）か。 */
  isStale: boolean;
}

/**
 * 保存形式として妥当か。
 *
 * fetchedAt は Number.isFinite で見る（typeof === 'number' では NaN / Infinity が
 * 通ってしまい、経過時間の比較が両方とも false になって「二度と再取得されない
 * キャッシュ」が生まれるため）。ここを通れば以降の計算は必ず有限の数値になる。
 */
const isStoredMasterCache = <T>(parsed: unknown): parsed is StoredMasterCache<T> => {
  if (parsed === null || typeof parsed !== 'object') return false;
  const candidate = parsed as Partial<StoredMasterCache<T>>;
  return Number.isFinite(candidate.fetchedAt) && 'data' in candidate;
};

/**
 * マスタ系キャッシュを読み出す。
 *
 * 値の妥当性検証（Array.isArray 等）は呼び出し側の既存チェックに委ねる。
 *
 * @param key キャッシュキー
 * @returns キャッシュが無い/読めない/想定外の形式の場合は null
 */
export async function getMasterCache<T>(key: string): Promise<MasterCacheResult<T> | null> {
  try {
    const raw = await indexedDBStorage.getItem(key);
    if (!raw) return null;

    const parsed = JSON.parse(raw);
    if (!isStoredMasterCache<T>(parsed)) return null;

    // 端末の時計が巻き戻る（fetchedAt が未来になる）と経過時間が負になり、
    // 単純な >= 判定では永久に「新鮮」と見なされて再取得されなくなる。
    // 負なら時計が信用できない状態なので、素直に古い扱いにして取り直す。
    const age = Date.now() - parsed.fetchedAt;
    return {
      data: parsed.data,
      isStale: age < 0 || age >= MASTER_CACHE_TTL_MS,
    };
  } catch {
    // パース失敗・IndexedDB エラー時はキャッシュ無し扱い（呼び出し側がネットワーク取得へ）
    return null;
  }
}

/**
 * 取得中の fetch を共有するためのマップ（キー＝キャッシュキー）。
 * 型は呼び出しごとに異なるため unknown で持ち、dedupeFetch 側でキャストする。
 */
const inFlight = new Map<string, Promise<unknown>>();

/**
 * 同じキーの取得が既に進行中なら、その Promise を共有する（in-flight 重複排除）。
 *
 * getMasterCache はキャッシュが「書き終わってから」しか効かないため、同一マスタを使う
 * コンポーネントが同時にマウントされると、キャッシュ未生成時に同じ API が2本同時に飛ぶ。
 * 往復を減らすのが目的の仕組みなので、ここで1本に畳む。
 *
 * 呼び出し側には fetcher の Promise をそのまま返すため、戻り値・例外の契約は変わらない
 * （getOptionFieldList はキャッシュミス時に throw する契約なので、これが必要）。
 *
 * @param key キャッシュキー（同じキーの取得同士だけが畳まれる）
 * @param fetcher ネットワーク取得の本体
 */
export function dedupeFetch<T>(key: string, fetcher: () => Promise<T>): Promise<T> {
  const existing = inFlight.get(key);
  if (existing) return existing as Promise<T>;

  const pending = fetcher();
  inFlight.set(key, pending);

  // 成否どちらでも取り除く。then に成功・失敗の両ハンドラを渡しているので、
  // ここで派生する Promise は必ず解決し、unhandled rejection にはならない
  // （finally だと元の rejection がそのまま伝播して未処理扱いになりうる）。
  const cleanup = () => {
    // 後続の取得が既に登録されている可能性があるため、自分の Promise の時だけ消す
    if (inFlight.get(key) === pending) inFlight.delete(key);
  };
  pending.then(cleanup, cleanup);

  return pending;
}

/**
 * マスタ系キャッシュへデータと取得時刻をまとめて保存する。
 *
 * データと取得時刻が食い違わないよう、必ず1つの値として書く。
 *
 * @param key キャッシュキー
 * @param data 保存するデータ
 */
export async function setMasterCache<T>(key: string, data: T): Promise<void> {
  const stored: StoredMasterCache<T> = {
    data,
    fetchedAt: Date.now(),
  };
  await indexedDBStorage.setItem(key, JSON.stringify(stored));
}

/**
 * キャッシュされた値がオブジェクト形式か（配列も含む緩い判定）。
 * statuscode 等の詳細検証は呼び出し側に委ねる、最低限の形式チェック用。
 */
export const isObjectData = <T>(d: unknown): d is T => !!d && typeof d === 'object';

/**
 * マスタ系キャッシュの定型取得手順（cache-first + TTL revalidate + in-flight 重複排除）をまとめる。
 *
 * @param key キャッシュキー
 * @param isValid 読み出したキャッシュの data が使える形か（Array.isArray 等、呼び出し側の既存チェック）
 * @param fetchFresh ネットワーク取得の本体（成功時は自身で setMasterCache する契約）
 * @param onFresh 裏の再取得が成功したときにだけ呼ばれる（省略時は何もしない＝裏取得結果を画面に返さない）。
 *   目的／アポイントのように defaultflag の自動選択などライブ反映が必要な呼び出し元だけが使う想定。
 */
export async function withMasterCache<T>(
  key: string,
  isValid: (data: unknown) => data is T,
  fetchFresh: () => Promise<T>,
  onFresh?: (data: T) => void,
): Promise<T> {
  const cached = await getMasterCache<T>(key);
  if (cached && isValid(cached.data)) {
    if (cached.isStale) {
      dedupeFetch(key, fetchFresh).then((fresh) => onFresh?.(fresh)).catch(() => {});
    }
    return cached.data;
  }
  return dedupeFetch(key, fetchFresh);
}
